import { getValidationNames } from '@/modules/common/lib/application-review';
import {
  ApplicationValidationReviewer,
  ApplicationValidationStatus,
  ApplicationValidationType,
} from '@/types/application';
import { UserRole } from '@/types/user';
import { EducationProviderReviewModal } from './EducationProviderReviewModal';
import { EmployerLearningBudgetReviewModal } from './EmployerLearningBudgetReviewModal';
import { PaymentFeaturesReviewModal } from './PaymentFeaturesReviewModal';
import { ReviewModalProps, ReviewModalState } from './ReviewModal.types';
import { TaxBenefitsReviewModal } from './TaxBenefitsReviewModal';

export function ReviewModal({
  applicationsToReview,
  userRoles,
  initialReview,
  onClose,
  ...props
}: ReviewModalProps) {
  const validationsToReview = getValidationNames(
    applicationsToReview,
    userRoles,
  );

  const isSoloLearningBudgetReview =
    applicationsToReview.length === 1 &&
    userRoles.includes(UserRole.Employer) &&
    applicationsToReview[0].validations?.some(
      validation =>
        validation.name === ApplicationValidationType.LearningBudget &&
        validation.reviews.some(
          review =>
            review.reviewer === ApplicationValidationReviewer.Employer &&
            review.status === ApplicationValidationStatus.Pending,
        ),
    );

  const isSoloTaxBenefitsReview =
    applicationsToReview.length === 1 &&
    [UserRole.TaxBenefitsProvider, UserRole.Employer].some(role =>
      userRoles.includes(role),
    ) &&
    applicationsToReview[0].validations?.some(
      validation =>
        validation.name === ApplicationValidationType.FlexibleRemuneration &&
        validation.reviews.some(
          review => review.status === ApplicationValidationStatus.Pending,
        ),
    );

  return userRoles.includes(UserRole.EducationProvider) ? (
    <EducationProviderReviewModal
      applicationsToReview={applicationsToReview}
      userRoles={userRoles}
      onClose={onClose}
      validationsToReview={validationsToReview}
      {...props}
    />
  ) : isSoloLearningBudgetReview ? (
    <EmployerLearningBudgetReviewModal
      application={applicationsToReview[0]}
      onClose={onClose}
      {...props}
    />
  ) : isSoloTaxBenefitsReview ? (
    <TaxBenefitsReviewModal
      application={applicationsToReview[0]}
      onClose={onClose}
      userRoles={userRoles}
      {...props}
    />
  ) : (
    <PaymentFeaturesReviewModal
      applicationsToReview={applicationsToReview}
      userRoles={userRoles}
      initialReview={{
        ...Object.entries(initialReview).reduce(
          (acc, [k]) => ({
            ...acc,
            [k]: null, // force the user to select a value
          }),
          {} as ReviewModalState,
        ),
      }}
      onClose={onClose}
      validationsToReview={validationsToReview}
      {...props}
    />
  );
}
